// app/login/page.tsx
"use client";

import { useState, useEffect, useCallback, useRef, Suspense } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  ArrowRight,
  Loader2,
  ChefHat,
  ShieldCheck,
  User,
  Clock,
  AlertTriangle,
} from "lucide-react";
import { SM_Header } from "@/components/small-nav";
import { useAuthStore } from "@/lib/stores/auth.store";
import { useUser } from "@/context/UserContext";
import { notify } from "@/lib/notification/toast-manager";

// ============================================================================
// DESIGN TOKENS
// ============================================================================

const DESIGN_TOKENS = {
  colors: {
    primary: "#2F4858",
    secondary: "#FF8C42",
    accent: "#FFE156",
    success: "#4CAF50",
    error: "#F44336",
    warning: "#FF9800",
    info: "#2196F3",
    background: "#F5F5F7",
    card: "#FFFFFF",
    text: {
      primary: "#1D1D1F",
      secondary: "#86868B",
      tertiary: "#8E8E93",
    },
    border: {
      light: "#E5E5E7",
      medium: "#D1D1D6",
    },
    overlay: "rgba(0, 0, 0, 0.05)",
  },
  spacing: {
    xs: "0.75rem",
    sm: "1rem",
    md: "1.5rem",
    lg: "2rem",
    xl: "3rem",
  },
  borderRadius: {
    sm: "12px",
    md: "18px",
    lg: "24px",
    xl: "32px",
    pill: "9999px",
  },
  typography: {
    h1: "2.5rem",
    h2: "2rem",
    h3: "1.5rem",
    body: "1.125rem",
    caption: "1rem",
    small: "0.875rem",
  },
  shadows: {
    subtle: "0 4px 20px rgba(0, 0, 0, 0.04)",
    medium: "0 8px 32px rgba(0, 0, 0, 0.08)",
    floating: "0 16px 48px rgba(0, 0, 0, 0.12)",
  },
};

const SECURITY_CONFIG = {
  MAX_ATTEMPTS: 5,
  LOCKOUT_DURATION: 15 * 60 * 1000,
  PASSWORD_MIN_LENGTH: 8,
};

// ============================================================================
// COMPONENTS
// ============================================================================

const LoadingSpinner = ({ size = 24 }: { size?: number }) => (
  <div className="flex items-center justify-center animate-spin">
    <Loader2 size={size} className="text-current" />
  </div>
);

interface InputFieldProps {
  id?: string;
  label?: string;
  type: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder: string;
  disabled?: boolean;
  autoComplete?: string;
  error?: boolean;
  showPasswordToggle?: boolean;
  showPassword?: boolean;
  onTogglePassword?: () => void;
  required?: boolean;
  icon?: React.ReactNode;
  className?: string;
}

const InputField = ({
  id,
  label,
  type,
  value,
  onChange,
  placeholder,
  disabled = false,
  autoComplete,
  error = false,
  showPasswordToggle = false,
  showPassword = false,
  onTogglePassword,
  required = true,
  icon,
  className = "",
}: InputFieldProps) => {
  const [isFocused, setIsFocused] = useState(false);

  const getBorderColor = () => {
    if (error) return DESIGN_TOKENS.colors.error;
    if (isFocused) return DESIGN_TOKENS.colors.primary;
    return DESIGN_TOKENS.colors.border.light;
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <label 
          htmlFor={id} 
          style={{ color: DESIGN_TOKENS.colors.text.primary }}
          className="block text-base font-medium"
        >
          {label}
          {required && <span style={{ color: DESIGN_TOKENS.colors.error }} className="ml-1">*</span>}
        </label>
      )}
      <div className="relative">
        {icon && (
          <div style={{ color: DESIGN_TOKENS.colors.text.secondary }} className="absolute left-4 top-1/2 transform -translate-y-1/2">
            {icon}
          </div>
        )}
        <input
          id={id}
          type={showPasswordToggle && showPassword ? "text" : type}
          value={value}
          onChange={onChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          disabled={disabled}
          placeholder={placeholder}
          autoComplete={autoComplete}
          required={required}
          aria-invalid={error}
          style={{
            borderColor: getBorderColor(),
            backgroundColor: disabled ? DESIGN_TOKENS.colors.background : DESIGN_TOKENS.colors.card,
            color: DESIGN_TOKENS.colors.text.primary,
            borderRadius: DESIGN_TOKENS.borderRadius.md,
            paddingLeft: icon ? '3.5rem' : '1rem',
            paddingRight: showPasswordToggle ? '3.5rem' : '1rem',
          }}
          className={`
            w-full px-5 py-4 border-2 outline-none transition-all text-base
            focus:ring-3 focus:ring-opacity-20 focus:ring-offset-0
            ${disabled ? 'cursor-not-allowed opacity-60' : ''}
            placeholder:text-gray-400
            font-normal
          `}
        />
        
        {showPasswordToggle && onTogglePassword && (
          <button
            type="button"
            style={{ color: DESIGN_TOKENS.colors.text.secondary }}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 hover:opacity-70 transition-opacity p-1.5"
            onClick={onTogglePassword}
            disabled={disabled}
            tabIndex={-1}
            aria-label={showPassword ? "Hide password" : "Show password"}
            aria-controls={id}
          >
            {showPassword ? <EyeOff size={22} /> : <Eye size={22} />}
          </button>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// MAIN LOGIN COMPONENT
// ============================================================================

function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  
  const formRef = useRef<HTMLFormElement>(null);
  const mounted = useRef(true);
  
  const [formState, setFormState] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{ email?: string; password?: string }>({});
  const [lockoutTime, setLockoutTime] = useState<number | null>(null);
  
  const authUI = useAuthStore();
  const { isLoading, error: storeError, lastLoginAttempt, loginAttempts, setLoading, setError: setStoreError, recordLoginFailure, resetLoginAttempts } = authUI;
  const { login } = useUser();

  const isRateLimited = (() => {
    if (!lastLoginAttempt || loginAttempts < SECURITY_CONFIG.MAX_ATTEMPTS) return false;
    return Date.now() - lastLoginAttempt < SECURITY_CONFIG.LOCKOUT_DURATION;
  })();

  const remainingLockoutTime = (() => {
    if (!lastLoginAttempt || loginAttempts < SECURITY_CONFIG.MAX_ATTEMPTS) return 0;
    return Math.max(0, SECURITY_CONFIG.LOCKOUT_DURATION - (Date.now() - lastLoginAttempt));
  })();

  // Effects
  useEffect(() => {
    mounted.current = true;
    
    const savedEmail = localStorage.getItem("indeMenuUserEmail");
    if (savedEmail) {
      setFormState(prev => ({ ...prev, email: savedEmail }));
      setRememberMe(true);
    }
    
    const reason = searchParams.get('reason');
    const redirect = searchParams.get('redirect');
    
    if (redirect) {
      sessionStorage.setItem('loginRedirect', redirect);
    }
    
    return () => {
      mounted.current = false;
    };
  }, [router, searchParams]);

  useEffect(() => {
    if (!isRateLimited) {
      setLockoutTime(null);
      return;
    }
    
    setLockoutTime(remainingLockoutTime);
    
    const interval = setInterval(() => {
      if (!mounted.current) return;
      setLockoutTime(prev => prev ? Math.max(0, prev - 1000) : null);
    }, 1000);
    
    return () => clearInterval(interval);
  }, [isRateLimited, remainingLockoutTime]);

  // Utilities
  const validateEmailFormat = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleInputChange = useCallback((field: 'email' | 'password') => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormState(prev => ({ ...prev, [field]: value }));
    
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: undefined }));
    }
    
    if (storeError) setStoreError(null);
  }, [validationErrors, storeError, setStoreError]);

  const validateForm = useCallback((): boolean => {
    const errors: { email?: string; password?: string } = {};
    
    if (!formState.email.trim()) {
      errors.email = "Email is required";
    } else if (!validateEmailFormat(formState.email)) {
      errors.email = "Please enter a valid email address";
    }
    
    if (!formState.password) {
      errors.password = "Password is required";
    } else if (formState.password.length < SECURITY_CONFIG.PASSWORD_MIN_LENGTH) {
      errors.password = `Password must be at least ${SECURITY_CONFIG.PASSWORD_MIN_LENGTH} characters`;
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formState]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!mounted.current) return;
    
    if (isRateLimited) {
      notify.error(`Account is temporarily locked. Please try again later.`);
      return;
    }
    
    if (!validateForm()) {
      const firstError = Object.values(validationErrors)[0];
      if (firstError) notify.error(firstError);
      return;
    }
    
    try {
      setLoading(true);
      
      if (rememberMe) {
        localStorage.setItem('indeMenuUserEmail', formState.email);
      } else {
        localStorage.removeItem('indeMenuUserEmail');
      }
      
      const success = await login({ 
        email: formState.email, 
        password: formState.password, 
        rememberMe 
      });
      
      if (success) {
        resetLoginAttempts();
        notify.success('Login successful!');
        
        const redirectPath = sessionStorage.getItem('loginRedirect') || '/';
        sessionStorage.removeItem('loginRedirect');
        
        setTimeout(() => {
          if (mounted.current) {
            router.replace(redirectPath);
          }
        }, 1000);
        
      } else {
        recordLoginFailure();
        
        const errObj = (storeError && typeof storeError === 'object')
          ? (storeError as { message?: string; errors?: { code?: string }[] })
          : null;

        if (errObj?.errors?.some((item: { code?: string }) => item.code === 'unverified' || item.code === 'EMAIL_NOT_VERIFIED')) {
          notify.warning('Please verify your email before logging in.');
          setTimeout(() => {
            router.push(`/verify-email?email=${encodeURIComponent(formState.email)}`);
          }, 1500);
        } else if (errObj?.errors?.some((item: { code?: string }) => item.code === 'RATE_LIMIT_EXCEEDED')) {
          notify.error('Too many login attempts. Please try again later.');
        } else {
          const fallbackMessage = typeof storeError === 'string' ? storeError : errObj?.message;
          notify.error(fallbackMessage || 'Invalid email or password');
        }
      }
    } catch (error: any) {
      recordLoginFailure();
      
      notify.error(error.message || 'An error occurred. Please try again.');
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [
    formState,
    rememberMe,
    isRateLimited,
    validateForm,
    setLoading,
    router,
    resetLoginAttempts,
    recordLoginFailure,
    validationErrors,
    login,
    storeError,
  ]);

  const formatTime = (ms: number) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div style={{ backgroundColor: DESIGN_TOKENS.colors.background }} className="min-h-screen">
      <SM_Header />
      
      <main className="container mx-auto px-4 py-12 lg:py-16">
        <div className="max-w-lg mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center gap-4 mb-8">
              <div 
                style={{ 
                  backgroundColor: DESIGN_TOKENS.colors.card,
                  borderRadius: DESIGN_TOKENS.borderRadius.lg,
                  boxShadow: DESIGN_TOKENS.shadows.subtle,
                }}
                className="p-4"
              >
                <ChefHat size={36} style={{ color: DESIGN_TOKENS.colors.primary }} />
              </div>
              <div className="text-left">
                <span 
                  style={{ 
                    color: DESIGN_TOKENS.colors.primary
                  }}
                  className="text-3xl font-bold block"
                >
                  IndeMenu
                </span>
                <span 
                  style={{ 
                    color: DESIGN_TOKENS.colors.text.secondary
                  }}
                  className="text-base font-medium block mt-1"
                >
                  Restaurant Management
                </span>
              </div>
            </div>
            <h1 style={{ color: DESIGN_TOKENS.colors.text.primary }} className="text-3xl font-bold mb-3">
              Welcome Back
            </h1>
            <p style={{ color: DESIGN_TOKENS.colors.text.secondary }} className="text-lg">
              Sign in to access your restaurant dashboard
            </p>
          </div>

          {/* Login Card */}
          <div
            style={{ 
              backgroundColor: DESIGN_TOKENS.colors.card,
              borderRadius: DESIGN_TOKENS.borderRadius.lg,
              boxShadow: DESIGN_TOKENS.shadows.medium,
            }}
            className="p-8 border border-gray-100"
          >
            {/* Lockout Warning */}
            {isRateLimited && lockoutTime !== null && (
              <div
                style={{ 
                  backgroundColor: `${DESIGN_TOKENS.colors.secondary}15`,
                  borderColor: `${DESIGN_TOKENS.colors.secondary}30`,
                  borderRadius: DESIGN_TOKENS.borderRadius.md,
                }}
                className="mb-6 p-5 border"
              >
                <div className="flex items-start gap-4">
                  <Clock style={{ color: DESIGN_TOKENS.colors.secondary }} className="flex-shrink-0 mt-0.5" size={22} />
                  <div className="flex-1">
                    <p style={{ color: DESIGN_TOKENS.colors.secondary }} className="font-bold text-base">
                      Account Temporarily Locked
                    </p>
                    <p style={{ color: DESIGN_TOKENS.colors.text.secondary }} className="text-sm mt-1.5">
                      Too many failed login attempts. Please try again in:
                    </p>
                    <div className="mt-4 flex items-center justify-between">
                      <div 
                        className="px-4 py-3 rounded-xl"
                        style={{ 
                          backgroundColor: DESIGN_TOKENS.colors.background 
                        }}
                      >
                        <span style={{ color: DESIGN_TOKENS.colors.secondary }} className="font-mono font-bold text-xl">
                          {formatTime(lockoutTime)}
                        </span>
                      </div>
                      <div className="text-right">
                        <p style={{ color: DESIGN_TOKENS.colors.secondary }} className="text-sm font-medium">
                          Attempts: {loginAttempts}/{SECURITY_CONFIG.MAX_ATTEMPTS}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Store Error */}
            {storeError && !isRateLimited && (
              <div
                style={{ 
                  backgroundColor: `${DESIGN_TOKENS.colors.error}15`,
                  borderColor: `${DESIGN_TOKENS.colors.error}30`,
                  borderRadius: DESIGN_TOKENS.borderRadius.md,
                }}
                className="mb-6 p-5 border"
                role="alert"
              >
                <div className="flex items-start gap-4">
                  <AlertTriangle style={{ color: DESIGN_TOKENS.colors.error }} className="flex-shrink-0 mt-0.5" size={22} />
                  <div className="flex-1">
                    <p style={{ color: DESIGN_TOKENS.colors.error }} className="font-bold text-base">Login Error</p>
                    <p style={{ color: DESIGN_TOKENS.colors.error }} className="text-sm mt-1">{storeError}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Form */}
            <form ref={formRef} onSubmit={handleSubmit} className="space-y-6" noValidate>
              {/* Email Field */}
              <InputField
                id="email"
                label="Email Address"
                type="email"
                value={formState.email}
                onChange={handleInputChange('email')}
                placeholder="you@restaurant.com"
                disabled={isLoading || isRateLimited}
                autoComplete="email"
                error={!!validationErrors.email}
                required
                icon={<Mail size={20} />}
              />
              {validationErrors.email && (
                <p 
                  style={{ color: DESIGN_TOKENS.colors.error }}
                  className="text-sm font-medium ml-1"
                >
                  {validationErrors.email}
                </p>
              )}

              {/* Password Field */}
              <InputField
                id="password"
                label="Password"
                type="password"
                value={formState.password}
                onChange={handleInputChange('password')}
                placeholder="Enter your password"
                disabled={isLoading || isRateLimited}
                autoComplete="current-password"
                error={!!validationErrors.password}
                showPasswordToggle
                showPassword={showPassword}
                onTogglePassword={() => setShowPassword(!showPassword)}
                required
                icon={<Lock size={20} />}
              />
              {validationErrors.password && (
                <p 
                  style={{ color: DESIGN_TOKENS.colors.error }}
                  className="text-sm font-medium ml-1"
                >
                  {validationErrors.password}
                </p>
              )}

              {/* Options */}
              <div className="flex items-center justify-between">
                <label className="flex items-center text-base cursor-pointer group"
                  style={{ color: DESIGN_TOKENS.colors.text.primary }}>
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    disabled={isLoading || isRateLimited}
                    style={{
                      borderColor: rememberMe ? DESIGN_TOKENS.colors.primary : DESIGN_TOKENS.colors.border.medium,
                      backgroundColor: rememberMe ? DESIGN_TOKENS.colors.primary : DESIGN_TOKENS.colors.card,
                    }}
                    className="h-5 w-5 rounded border-2 focus:ring-3 focus:ring-opacity-20 focus:ring-offset-0 transition-colors"
                  />
                  <span className="ml-3 group-hover:opacity-80 transition-opacity">
                    Remember me
                  </span>
                </label>
                <Link
                  href="/forgot-password"
                  style={{ color: DESIGN_TOKENS.colors.primary }}
                  className="text-base font-medium hover:opacity-80 transition-opacity hover:underline"
                  aria-label="Forgot password"
                >
                  Forgot password?
                </Link>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading || isRateLimited}
                style={{ 
                  backgroundColor: DESIGN_TOKENS.colors.primary,
                  borderRadius: DESIGN_TOKENS.borderRadius.md,
                }}
                className={`
                  w-full py-4 px-8 font-semibold text-white text-base
                  transition-all duration-200 flex items-center justify-center gap-3
                  ${isLoading || isRateLimited
                    ? 'opacity-60 cursor-not-allowed' 
                    : 'hover:opacity-90 active:opacity-80 active:scale-[0.99]'
                  }
                `}
              >
                {isLoading ? (
                  <>
                    <LoadingSpinner size={20} />
                    <span>Signing In...</span>
                  </>
                ) : isRateLimited ? (
                  <>
                    <Clock size={20} />
                    <span>Temporarily Locked</span>
                  </>
                ) : (
                  <>
                    <span>Sign In</span>
                    <ArrowRight size={20} />
                  </>
                )}
              </button>
            </form>

            {/* Divider */}
            <div className="flex items-center my-8">
              <div className="flex-1 h-px" style={{ backgroundColor: DESIGN_TOKENS.colors.border.light }} />
              <span style={{ color: DESIGN_TOKENS.colors.text.tertiary }} className="px-4 text-sm font-medium">
                Don't have an account?
              </span>
              <div className="flex-1 h-px" style={{ backgroundColor: DESIGN_TOKENS.colors.border.light }} />
            </div>

            {/* Create Account Button */}
            <Link
              href="/signup"
              style={{ 
                borderColor: DESIGN_TOKENS.colors.border.light,
                borderRadius: DESIGN_TOKENS.borderRadius.md,
              }}
              className={`
                w-full py-4 px-8 font-semibold text-base border-2
                transition-all duration-200 flex items-center justify-center gap-3
                hover:border-opacity-60 active:scale-[0.99]
              `}
            >
              <User size={20} style={{ color: DESIGN_TOKENS.colors.primary }} />
              <span style={{ color: DESIGN_TOKENS.colors.primary }}>Create New Account</span>
            </Link>

            {/* Security Badge */}
            <div 
              className="mt-8 p-4 rounded-xl flex items-center justify-center gap-3"
              style={{ 
                backgroundColor: DESIGN_TOKENS.colors.background,
                borderRadius: DESIGN_TOKENS.borderRadius.md,
              }}
            >
              <ShieldCheck size={20} style={{ color: DESIGN_TOKENS.colors.success }} />
              <span style={{ color: DESIGN_TOKENS.colors.text.secondary }} className="text-sm font-medium">
                End-to-end encrypted • Secure login
              </span>
            </div>
          </div>

          {/* Footer */}
          <div
            style={{ color: DESIGN_TOKENS.colors.text.secondary }}
            className="mt-8 text-center text-sm"
          >
            <div className="space-y-3">
              <p>
                By signing in, you agree to our{' '}
                <Link 
                  href="/terms" 
                  style={{ color: DESIGN_TOKENS.colors.primary }}
                  className="font-medium hover:underline hover:opacity-80 transition-opacity"
                >
                  Terms of Service
                </Link>
                {' '}and{' '}
                <Link 
                  href="/privacy" 
                  style={{ color: DESIGN_TOKENS.colors.primary }}
                  className="font-medium hover:underline hover:opacity-80 transition-opacity"
                >
                  Privacy Policy
                </Link>
              </p> 
              <p className="text-xs" style={{ color: DESIGN_TOKENS.colors.text.tertiary }}>
                Need help?{' '}
                <Link 
                  href="/pages/contact" 
                  style={{ color: DESIGN_TOKENS.colors.primary }}
                  className="font-medium hover:underline"
                >
                  Contact Support
                </Link>
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={null}>
      <LoginForm />
    </Suspense>
  );
}